import clsx from 'clsx';
import {
  type ChangeEvent,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  memo,
} from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
  _mergeProps,
  _useAriaLabelingProps,
  _useSafeForwardProps,
} from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { _textStyleCSS as textStyleCSS } from '@dynatrace/strato-components/styles';
import { CheckmarkSmallIcon, MinusIcon } from '@dynatrace/strato-icons';

import {
  checkboxIconCSS,
  checkboxKnobCSS,
  checkboxLabelCSS,
} from './Checkbox.sty.js';
import { VisuallyHidden } from '../../core/components/visually-hidden/VisuallyHidden.js';
import { useControllableState } from '../../core/hooks/useControllableState.js';
import { useFormControlState } from '../../core/hooks/useFormControlState.js';
import { useFocusRing } from '../../core/styles/useFocusRing.js';
import type { FocusEvents } from '../../core/types/events.js';
import type { FormControlProps } from '../../core/types/form-control-props.js';
import { fieldColorsCSS } from '../../styles/field.sty.js';
import { InternalBaseItem as BaseItem } from '../forms-core/components/form-field-messages/FormFieldMessageBaseItem.js';
import { useFormFieldMessagesContext } from '../forms-core/hooks/useFormFieldMessagesContext.js';
import { useFormFieldValidation } from '../forms-core/hooks/useFormFieldValidation.js';
import { useSyncedFormControlProps } from '../forms-core/hooks/useSyncedFormControlProps.js';
import { FormFieldMessagesAction } from '../forms-core/state/formfield-messages-state-reducer.js';
import { useInputHint } from '../hooks/useInputHint.js';
import type { FormControlRef } from '../shared-types.js';

// #region Typings
/**
 * @public
 */
export type CheckboxProps = FormControlProps<
  boolean | 'indeterminate',
  (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void | never
> &
  FocusEvents &
  StylingProps &
  DataTestId &
  WithChildren &
  MaskingProps & {
    /**
     * A string representing the value used for the checkbox.
     * When submitted in a form, it is only submitted if checked, with the set value.
     * If undefined, the string 'on' is submitted by default as per https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/checkbox#attr-value.
     */
    formValue?: string;
  };
// #endregion

/**
 * Checkboxes allow the user to select one or more options from a list of options.
 * @public
 */
export const Checkbox = forwardRef<FormControlRef, CheckboxProps>(
  (props, forwardedRef) => {
    const {
      disabled: propDisabled,
      required: propRequired,
      readOnly = false,
      id: propId,
      defaultValue,
      value,
      name,
      formValue,
      onChange,
      children,
      controlState,
      className: consumerClassName,
      style: consumerStyle,
      'aria-disabled': ariaDisabledProp,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: CheckboxProps = props;

    const ariaLabelingProps = _useAriaLabelingProps(remainingProps);
    const forwardProps = _useSafeForwardProps(
      remainingProps,
      ariaLabelingProps,
    );

    const { state: messagesContextState, dispatch: messagesContextDispatch } =
      useFormFieldMessagesContext();

    // Imperative Handle Construction
    const rootRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const {
      onChange: validationOnChange,
      onBlur: validationOnBlur,
      onInvalid: validationOnInvalid,
    } = useFormFieldValidation(inputRef);

    useImperativeHandle(forwardedRef, (): FormControlRef => {
      return {
        element: rootRef.current,
        inputRef: inputRef.current,
        focus() {
          inputRef.current?.focus();
        },
        validate() {
          if (messagesContextState) {
            inputRef.current?.checkValidity();
            messagesContextDispatch?.({
              type: FormFieldMessagesAction.SETISTOUCHED,
              payload: { isTouched: true },
            });
          }
        },
      };
    });

    const [state, setState] = useControllableState({
      defaultValue,
      value,
    });

    const generatedUniqueId = useId();
    const {
      disabled,
      required,
      id: contextOrPropId,
      ariaDisabled,
    } = useSyncedFormControlProps({
      propDisabled,
      propRequired,
      propId,
      propAriaDisabled: ariaDisabledProp,
    });
    const id = contextOrPropId ?? generatedUniqueId;
    const disabledOrAriaDisabled = disabled || ariaDisabled || false;

    const [hasError, hint] = useFormControlState(controlState);
    // _useInputHint will be replaced with new validation concept. APPDEV-8298
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    const { inputProps, hintProps } = useInputHint({
      hint,
      hasError,
    });

    const showError = hasError || messagesContextState.showError;

    let focusRingColor: 'neutral' | 'critical' | 'primary' = 'neutral';
    if (showError) {
      focusRingColor = 'critical';
    } else if (!disabled && state) {
      focusRingColor = 'primary';
    }

    const { focusProps, focusClassName } = useFocusRing({
      color: focusRingColor,
      disabled,
    });

    const handleOnChange = (e: ChangeEvent<HTMLInputElement>): void => {
      if (readOnly) {
        return;
      }

      const newValue = e.target.indeterminate ? true : e.target.checked;
      setState(newValue);
      onChange?.(newValue, e);
      validationOnChange();

      if (!messagesContextState.isTouched) {
        messagesContextDispatch?.({
          type: FormFieldMessagesAction.SETISTOUCHED,
          payload: { isTouched: true },
        });
      }
    };

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.indeterminate = value === 'indeterminate';
      }
    }, [value]);

    let fieldColorVariant: 'default' | 'accent' | 'emphasized' = 'default';
    if (state === true || state === 'indeterminate') {
      fieldColorVariant = 'accent';
    } else if (showError || disabledOrAriaDisabled) {
      fieldColorVariant = 'emphasized';
    }

    let fieldColorColor: 'neutral' | 'primary' | 'critical' = 'primary';
    if (showError) {
      fieldColorColor = 'critical';
    } else if (state === false) {
      fieldColorColor = 'neutral';
    }

    return (
      <Flex
        ref={rootRef}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        flexDirection="column"
        gap={4}
        className={consumerClassName}
        style={consumerStyle}
        {...forwardProps}
      >
        <label
          htmlFor={id}
          className={clsx(
            checkboxLabelCSS({
              required: typeof children === 'string' && required,
              disabled: disabledOrAriaDisabled,
              readOnly,
            }),
            textStyleCSS({ fontStyle: 'text', textStyle: 'base' }),
          )}
        >
          <div
            className={clsx(
              checkboxKnobCSS({
                disabled: disabledOrAriaDisabled,
                hasChildren: !!children,
                hasError: showError,
                checked: state === true || state === 'indeterminate',
                readOnly,
              }),
              fieldColorsCSS({
                variant: fieldColorVariant,
                disabled: disabledOrAriaDisabled,
                color: fieldColorColor,
              }),
              focusClassName,
            )}
          >
            <VisuallyHidden>
              <input
                id={id}
                ref={inputRef}
                type="checkbox"
                aria-checked={state === 'indeterminate' ? 'mixed' : state}
                aria-required={required}
                aria-disabled={ariaDisabled}
                readOnly={readOnly}
                name={name}
                value={formValue}
                disabled={disabled}
                required={required}
                checked={state === true}
                onChange={handleOnChange}
                onBlur={(event) => {
                  focusProps.onBlur(event);
                  validationOnBlur(event);
                }}
                onFocus={focusProps.onFocus}
                onInvalid={validationOnInvalid}
                {..._mergeProps(ariaLabelingProps, inputProps)}
              />
            </VisuallyHidden>
            <div className={checkboxIconCSS}>
              <CheckboxIconSvg state={state} />
            </div>
          </div>
          {children}
        </label>
        {hint && (
          <BaseItem
            variant={showError ? 'error' : 'hint'}
            disabled={disabledOrAriaDisabled}
            {...hintProps}
          >
            {hint}
          </BaseItem>
        )}
      </Flex>
    );
  },
);
(Checkbox as typeof Checkbox & { displayName: string }).displayName =
  'Checkbox';

type CheckboxIconProps = {
  state: boolean | 'indeterminate';
};

/** Determines what svg to display based on the checked and indeterminate attributes. */
const CheckboxIconSvg = memo((props: CheckboxIconProps) => {
  switch (props.state) {
    case true:
      return <CheckmarkSmallIcon />;
    case 'indeterminate':
      return <MinusIcon />;
    case false:
    default:
      return null;
  }
});
